import React, { useState } from 'react';
import { Edit2, Trash2, Save, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const ExcavationMaterialCard = ({ material, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedBankCY, setEditedBankCY] = useState(material.bankCubicYards);
  const [editedCostPerTon, setEditedCostPerTon] = useState(material.costPerTon || 0);

  const handleSave = () => {
    const bcy = parseFloat(editedBankCY);
    const cost = parseFloat(editedCostPerTon) || 0;
    if (bcy > 0) {
      onUpdate(material.id, { bankCubicYards: bcy, costPerTon: cost });
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditedBankCY(material.bankCubicYards);
    setEditedCostPerTon(material.costPerTon || 0);
    setIsEditing(false);
  };

  const totalCost = (material.totalTons || 0) * (material.costPerTon || 0);

  const getCategoryColor = (category) => {
    const colors = {
      'Soil & Earth': 'bg-amber-100 text-amber-800 border-amber-200',
      'Clay': 'bg-orange-100 text-orange-800 border-orange-200',
      'Sand & Gravel': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Rock': 'bg-slate-100 text-slate-800 border-slate-200',
      'Mixed Materials': 'bg-stone-100 text-stone-800 border-stone-200',
      'Organic Materials': 'bg-green-100 text-green-800 border-green-200',
      'Demolished Materials': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <Card className="border border-border hover:shadow-md transition-smooth bg-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-lg font-semibold text-foreground">{material.name}</h3>
              <Badge variant="outline" className={`${getCategoryColor(material.category)} border`}>
                {material.category}
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Tons/BCY</p>
                <p className="text-sm font-semibold text-foreground">
                  {material.tonsPerBCY} tons
                </p>
                <p className="text-xs text-muted-foreground">Swell: {material.swellFactor}x</p>
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Bank Volume</p>
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedBankCY}
                    onChange={(e) => setEditedBankCY(e.target.value)}
                    className="h-8 w-24 text-sm"
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                ) : (
                  <>
                    <p className="text-sm font-semibold text-foreground">
                      {material.bankCubicYards.toLocaleString()} BCY
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {material.looseCubicYards.toFixed(2)} LCY
                    </p>
                  </>
                )}
              </div>
              
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Weight</p>
                <p className="text-base font-bold text-primary">
                  {material.totalTons.toLocaleString(undefined, { maximumFractionDigits: 2 })} tons
                </p>
                <p className="text-xs text-muted-foreground">
                  {(material.totalTons * 2000).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                </p>
              </div>
              
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Cost/Ton</p>
                {isEditing ? (
                  <Input
                    type="number"
                    value={editedCostPerTon}
                    onChange={(e) => setEditedCostPerTon(e.target.value)}
                    className="h-8 w-full max-w-[100px] text-sm"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                ) : (
                  <p className="text-sm font-semibold text-foreground break-words">
                    ${(material.costPerTon || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Total Cost</p>
                <p className="text-base font-bold text-success break-words">
                  ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              
              <div className="flex items-end gap-2">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleSave}
                      className="bg-success hover:bg-success/90 h-8"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancel}
                      className="h-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="h-8 hover:bg-accent hover:text-accent-foreground"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDelete(material.id)}
                      className="h-8 border-destructive/50 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExcavationMaterialCard;